import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { SkeletonList } from "@/components/Skeletons";
import { Avatar } from "@/components/Avatar";
import { relativeDate, dayKey } from "@/lib/format";
import { toast } from "sonner";
import client from "@/lib/api";
import { Trash2, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnalysesList() {
    const navigate = useNavigate();
    const [items, setItems] = useState(null);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [q, setQ] = useState("");
    const [sort, setSort] = useState("recent");
    const pageSize = 10;

    const load = useCallback(async () => {
        setItems(null);
        const { data } = await client.get("/analyses", { params: { page, page_size: pageSize, q, sort } });
        setItems(data.items);
        setTotal(data.total);
    }, [page, q, sort]);

    useEffect(() => {
        const t = setTimeout(load, q ? 300 : 0);
        return () => clearTimeout(t);
    }, [load, q]);

    const remove = (a) => {
        setItems((prev) => prev.filter((x) => x.id !== a.id));
        setTotal((t) => t - 1);
        const timer = setTimeout(() => {
            client.delete(`/analyses/${a.id}`).catch(() => toast.error("No pudimos eliminarlo."));
        }, 5000);
        toast("Análisis eliminado", {
            description: "Puedes recuperarlo durante unos segundos.",
            action: {
                label: "Deshacer",
                onClick: () => {
                    clearTimeout(timer);
                    load();
                },
            },
        });
    };

    const pages = Math.ceil(total / pageSize) || 1;
    let lastKey = null;

    return (
        <>
            <PageHeader
                title="Mi piel"
                subtitle="Cada análisis es una foto de tu piel en un momento. Repítelos para ver tu evolución."
                action={
                    <Button asChild data-testid="analyses-new"><Link to="/app/analisis/nuevo">Nuevo análisis</Link></Button>
                }
            />

            <div className="mb-5 flex flex-wrap items-center gap-3">
                <Input placeholder="Buscar por tipo de piel…" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }}
                    className="max-w-xs" data-testid="analyses-search" />
                <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="analyses-sort"
                    className="h-10 rounded-lg border border-line bg-card px-3 text-[14px] text-ink outline-none focus:ring-2 focus:ring-ring">
                    <option value="recent">Más recientes</option>
                    <option value="old">Más antiguos</option>
                </select>
                <a href={`${API}/analyses/export.csv`} className="ml-auto">
                    <Button variant="outline" size="sm" data-testid="analyses-export">
                        <Download size={15} className="mr-1.5" /> Exportar CSV
                    </Button>
                </a>
            </div>

            {items === null ? (
                <SkeletonList rows={5} />
            ) : items.length === 0 ? (
                <EmptyState testId="analyses-empty" title="Todavía no hay análisis"
                    text="En cuanto hagas tu primer análisis aparecerá aquí, con su fecha y sus puntuaciones."
                    action={<Button asChild><Link to="/app/analisis/nuevo">Hacer mi primer análisis</Link></Button>} />
            ) : (
                <div className="space-y-4">
                    <div className="overflow-hidden rounded-lg border border-line bg-card">
                        {items.map((a) => {
                            const key = dayKey(a.created_at);
                            const showSep = key !== lastKey;
                            lastKey = key;
                            return (
                                <div key={a.id}>
                                    {showSep && (
                                        <div className="border-b border-line bg-background px-4 py-1.5 text-[12px] font-medium uppercase tracking-wide text-subtle">
                                            {key}
                                        </div>
                                    )}
                                    <div className="flex items-center gap-4 border-b border-line px-4 py-4 last:border-0 transition-colors duration-150 hover:bg-background/60"
                                        data-testid={`analysis-row-${a.id}`}>
                                        <Avatar name={a.skin_type} size={40} seed={a.id} />
                                        <button onClick={() => navigate(`/app/analisis/${a.id}`)} className="min-w-0 flex-1 text-left">
                                            <p className="truncate text-[15px] font-medium capitalize text-ink">Piel {a.skin_type}</p>
                                            <p className="truncate text-[13px] text-subtle">{a.summary}</p>
                                        </button>
                                        <span className="hidden text-[12px] text-subtle sm:block">{relativeDate(a.created_at)}</span>
                                        <button onClick={() => remove(a)} data-testid={`analysis-delete-${a.id}`}
                                            className="rounded-md p-2 text-subtle transition-colors duration-150 hover:bg-destructive/10 hover:text-destructive">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {pages > 1 && (
                        <div className="flex items-center justify-between">
                            <span className="text-[13px] text-subtle">Página {page} de {pages}</span>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                                <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Siguiente</Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
